import { compose } from "redux";
import React, { useRef } from "react";
import { connect } from "react-redux";
import { RootRef } from "@material-ui/core";
import { useDrag, useDrop } from "react-dnd";

import { invokeIfFunction } from "utils/app.util";
import { sortViewGroup } from "features/planner/planner.actions";

const DraggableViewGroup = ({
  type,
  viewId,
  disabled,
  viewColumn,
  viewGroupId,
  renderContent,
  viewColumnOrder,
  dispatchSortViewGroup,
}) => {
  const ref = useRef(null);

  const [, drop] = useDrop({
    accept: type.toString(),
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }

      // Don't replace items with themselves
      if (viewGroupId === item.viewGroupId) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current.getBoundingClientRect();
      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      // Determine mouse position
      const clientOffset = monitor.getClientOffset();
      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      // Dragging downwards
      if (
        item.viewColumnOrder < viewColumnOrder &&
        hoverClientY < hoverMiddleY
      ) {
        return;
      }

      // Dragging upwards
      if (
        item.viewColumnOrder > viewColumnOrder &&
        hoverClientY > hoverMiddleY
      ) {
        return;
      }

      dispatchSortViewGroup({
        viewId,
        groupType: type,
        sourceViewGroupId: item.viewGroupId,
        targetIndex: viewColumnOrder,
        sourceIndex: item.viewColumnOrder,
        targetViewColumn: viewColumn,
        sourceViewColumn: item.viewColumn,
      });

      item.viewColumnOrder = viewColumnOrder;
      item.viewColumn = viewColumn;

      return;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    item: {
      viewColumn,
      viewGroupId,
      viewColumnOrder,
      type: type.toString(),
    },
    canDrag: () => !disabled,
    collect: monitor => {
      let item;
      let isDragging = monitor.isDragging();

      if (!isDragging) {
        item = monitor.getItem();
      }

      const viewGroupHasChangedViewColumn =
        !!item &&
        item.viewGroupId === viewGroupId &&
        item.viewColumn !== viewColumn;

      return {
        isDragging: isDragging || viewGroupHasChangedViewColumn,
      };
    },
  });

  return (
    <RootRef rootRef={drag(drop(ref))}>
      {invokeIfFunction(renderContent, { isDragging })}
    </RootRef>
  );
};

export default compose(
  connect(null, {
    dispatchSortViewGroup: sortViewGroup,
  }),
  React.memo,
)(DraggableViewGroup);
